// src/components/PublicDashboard.js
// 公開唯讀儀表板 — 不需登入，透過 token URL 參數存取
import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { C } from '../theme'

function StatBox({ label, value, sub, color }) {
  return (
    <div style={{ background: C.panel, border: `1.5px solid ${C.border}`, borderRadius: 14, padding: '18px 20px', flex: 1, minWidth: 130 }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 800, color: color || C.accent, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

export default function PublicDashboard({ token }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    if (!token) { setError('invalid'); setLoading(false); return }
    load()
  }, [token])

  const load = async () => {
    setLoading(true)
    const { data: result, error: err } = await supabase.rpc('get_dashboard_data', { p_token: token })
    if (err || !result || result.error) {
      setError(result?.error || 'fetch_error')
      setLoading(false)
      return
    }
    setData(result)
    setLoading(false)
  }

  const fmt = (dt) => {
    if (!dt) return '-'
    const d = new Date(dt)
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 40 }}>🏭</div>
      <div style={{ color: C.muted, fontSize: 14 }}>載入中...</div>
    </div>
  )

  if (error === 'invalid_token' || error === 'invalid') return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <div style={{ color: C.text, fontSize: 18, fontWeight: 700 }}>連結無效</div>
      <div style={{ color: C.muted, fontSize: 13 }}>此儀表板連結已失效或不存在，請聯繫系統管理員重新產生。</div>
    </div>
  )

  if (error) return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 40 }}>⚠️</div>
      <div style={{ color: C.text, fontSize: 16, fontWeight: 700 }}>載入失敗</div>
      <button onClick={load} style={{ marginTop: 8, padding: '8px 20px', borderRadius: 8, background: C.accent, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14 }}>重試</button>
    </div>
  )

  const p = data.products || {}
  const today = data.records_today || {}
  const month = data.records_month || {}
  const recent = data.recent_records || []
  const lowStock = p.low_stock || []
  const monthLabel = `${now.getMonth() + 1}月`

  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <div style={{ background: C.panel, borderBottom: `1.5px solid ${C.border}`, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🏭</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>倉儲即時總覽</div>
            <div style={{ fontSize: 11, color: C.muted }}>唯讀模式 · 自動更新</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: C.muted }}>{now.toLocaleDateString('zh-TW', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'short' })}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.accent, fontVariantNumeric: 'tabular-nums' }}>
            {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '20px 16px 40px' }}>

        {/* 今日 */}
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 700, marginBottom: 10, letterSpacing: 1 }}>今日動態</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatBox label="今日入庫" value={today.in_count ?? 0} sub="筆" color={C.green} />
          <StatBox label="今日出庫" value={today.out_count ?? 0} sub="筆" color="#f59e0b" />
          <StatBox label="商品種類" value={p.total_count ?? 0} sub="種" color={C.accent} />
          <StatBox label="總庫存量" value={(p.total_stock ?? 0).toLocaleString()} sub="件" color={C.accent} />
        </div>

        {/* 本月 */}
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 700, marginBottom: 10, letterSpacing: 1 }}>{monthLabel}累計</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatBox label={`${monthLabel}入庫筆數`} value={month.in_count ?? 0} sub={`共 ${(month.in_qty ?? 0).toLocaleString()} 件`} color={C.green} />
          <StatBox label={`${monthLabel}出庫筆數`} value={month.out_count ?? 0} sub={`共 ${(month.out_qty ?? 0).toLocaleString()} 件`} color="#f59e0b" />
        </div>

        {/* 低庫存警示 */}
        {lowStock.length > 0 && (
          <>
            <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 700, marginBottom: 10, letterSpacing: 1 }}>⚠️ 低庫存警示</div>
            <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '12px 16px', marginBottom: 24 }}>
              {lowStock.map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i < lowStock.length - 1 ? '1px solid #fecaca' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#7f1d1d' }}>{item.name}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#dc2626' }}>{item.stock} {item.unit || '件'}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* 最近紀錄 */}
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 700, marginBottom: 10, letterSpacing: 1 }}>最近紀錄</div>
        <div style={{ background: C.panel, border: `1.5px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          {recent.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: C.muted, fontSize: 13 }}>目前無紀錄</div>
          ) : recent.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: i < recent.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', background: r.type === 'in' ? '#dcfce7' : '#fef3c7', fontSize: 18, flexShrink: 0 }}>
                {r.type === 'in' ? '📥' : '📤'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.product_name || '未知商品'}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{r.customer_name ? `${r.customer_name} · ` : ''}{r.note || ''}</div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: r.type === 'in' ? '#16a34a' : '#d97706' }}>
                  {r.type === 'in' ? '+' : '-'}{r.qty}
                </div>
                <div style={{ fontSize: 11, color: C.muted }}>{fmt(r.created_at)}</div>
              </div>
            </div>
          ))}
        </div>

        {/* 重新整理按鈕 */}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button onClick={load} style={{ padding: '10px 28px', borderRadius: 10, background: 'none', border: `1.5px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13 }}>
            🔄 重新整理
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: C.muted }}>
          此頁面為唯讀模式，僅供查看，無法進行任何操作
        </div>
      </div>
    </div>
  )
}
